import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaFlask, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import './EvaluationPanel.css';

// Evaluate a proposed change of an assistant's instructions before applying
// it (§14 C): the assistant's recent runs are replayed with the proposal
// (nothing is written to any ticket), a judge rates each replay, and the
// score decides. Used from the assistant page (free text) and from the
// feedback modal of a corrected draft (approved modifications).

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const POLL_MS = 3000;

const pct = (value) => (typeof value === 'number' ? `${Math.round(value * 100)} %` : '—');

const EvaluationPanel = ({ assistantId, initialInstructions = '', approvedModifications = null, sourceRunId = null,
    sourceMessageId = null, comment = null, onApplied, onEvaluated, compact = false }) => {
    const { t } = useTranslation();
    const [instructions, setInstructions] = useState(initialInstructions || '');
    const [sampleSize, setSampleSize] = useState(5);
    const [mode, setMode] = useState('call');
    const [evaluation, setEvaluation] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const poll = useCallback(async (id) => {
        try {
            const response = await axios.get(`/api/ai-assistants/${assistantId}/evaluations/${id}`, authHeaders());
            setEvaluation(response.data);
            if (response.data.status === 'queued' || response.data.status === 'running') {
                timerRef.current = setTimeout(() => poll(id), POLL_MS);
            } else if (onEvaluated) {
                onEvaluated(response.data);
            }
        } catch (e) {
            setError(t('evaluation.loadError', 'Impossible de suivre l’évaluation.'));
        }
    }, [assistantId, onEvaluated, t]);

    const launch = async () => {
        setError(null);
        setBusy(true);
        try {
            const body = { sample_size: sampleSize, mode, judge: true, source_run_id: sourceRunId, source_message_id: sourceMessageId, comment };
            if (approvedModifications) body.approved_modifications = approvedModifications;
            else body.instructions = instructions;
            const response = await axios.post(`/api/ai-assistants/${assistantId}/evaluations`, body, authHeaders());
            setEvaluation(response.data);
            if (response.data.status === 'queued' || response.data.status === 'running') {
                timerRef.current = setTimeout(() => poll(response.data.id), POLL_MS);
            }
        } catch (e) {
            setError(e?.response?.data?.detail || t('evaluation.launchError', 'Impossible de lancer l’évaluation.'));
        } finally {
            setBusy(false);
        }
    };

    const goldenBlocked = evaluation?.golden && evaluation.golden.pass_rate !== null && evaluation.golden.pass_rate !== undefined && evaluation.golden.gate_passed === false;

    const apply = async () => {
        if (!evaluation) return;
        setBusy(true);
        setError(null);
        try {
            const force = goldenBlocked && window.confirm(t('evaluation.goldenForceConfirm', 'Le jeu de référence échoue avec cette version. Appliquer malgré tout ?'));
            if (goldenBlocked && !force) { setBusy(false); return; }
            const response = await axios.post(`/api/ai-assistants/${assistantId}/evaluations/${evaluation.id}/apply${force ? '?force=true' : ''}`, {}, authHeaders());
            setEvaluation((prev) => ({ ...prev, applied_at: response.data.applied_at, applied_version: response.data.version }));
            if (onApplied) onApplied(response.data, evaluation);
        } catch (e) {
            setError(e?.response?.data?.detail || t('evaluation.applyError', 'Impossible d’appliquer cette version.'));
        } finally {
            setBusy(false);
        }
    };

    const score = evaluation?.score;
    const pending = evaluation && (evaluation.status === 'queued' || evaluation.status === 'running');
    const verdictGood = score && (score.same_decision_rate === null || score.same_decision_rate >= 0.8) && (score.tool_sequence_match_rate === null || score.tool_sequence_match_rate >= 0.8);

    return (
        <div className={`evaluation-panel ${compact ? 'compact' : ''}`}>
            {!evaluation && (
                <>
                    {!approvedModifications && (
                        <label className="evp-field">
                            <span>{t('evaluation.instructions', 'Instructions à évaluer')}</span>
                            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={compact ? 6 : 14} />
                        </label>
                    )}
                    <div className="evp-controls">
                        <label>
                            <span>{t('evaluation.sample', 'Runs rejoués')}</span>
                            <select value={sampleSize} onChange={(e) => setSampleSize(Number(e.target.value))}>
                                {[3, 5, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </label>
                        <label>
                            <span>{t('evaluation.mode', 'Mode')}</span>
                            <select value={mode} onChange={(e) => setMode(e.target.value)}>
                                <option value="call">{t('evaluation.modeCall', 'Appels du modèle (rapide)')}</option>
                                <option value="pipeline">{t('evaluation.modePipeline', 'Pipeline complet (plus fidèle)')}</option>
                            </select>
                        </label>
                        <button type="button" className="u-btn u-btn--primary" onClick={launch}
                            disabled={busy || (!approvedModifications && !instructions.trim())}>
                            <FaFlask /> {t('evaluation.launch', 'Lancer l’évaluation')}
                        </button>
                    </div>
                    <p className="evp-hint">{t('evaluation.hint', 'Les runs récents de cet assistant sont rejoués avec la proposition. Rien n’est écrit dans les tickets ; les outils ne sont pas exécutés.')}</p>
                </>
            )}
            {error && <div className="evp-error">{error}</div>}
            {evaluation && (
                <div className="evp-result">
                    <div className="evp-status">
                        {pending && <><FaSpinner className="spin" /> {t(`evaluation.status.${evaluation.status}`, evaluation.status)} · {evaluation.sample?.run_ids?.length || 0} {t('evaluation.runs', 'runs')}</>}
                        {evaluation.status === 'failed' && <><FaTimesCircle className="ko" /> {t('evaluation.status.failed', 'échec')} — {evaluation.error}</>}
                        {evaluation.status === 'done' && <><FaCheckCircle className={verdictGood ? 'ok' : 'warn'} /> {verdictGood ? t('evaluation.verdictGood', 'La proposition garde les mêmes décisions.') : t('evaluation.verdictWarn', 'La proposition change les décisions : à relire avant d’appliquer.')}</>}
                    </div>
                    {score && (
                        <div className="evp-scores">
                            <div><strong>{pct(score.same_decision_rate)}</strong><span>{t('evaluation.sameDecision', 'même décision')}</span></div>
                            <div><strong>{pct(score.same_actions_rate)}</strong><span>{t('evaluation.sameActions', 'mêmes actions')}</span></div>
                            <div><strong>{pct(score.tool_sequence_match_rate)}</strong><span>{t('evaluation.toolsMatch', 'outils identiques')}</span></div>
                            <div><strong>{pct(score.mean_diff_ratio)}</strong><span>{t('evaluation.drift', 'texte modifié')}</span></div>
                            <div><strong>{score.replayed}/{score.runs}</strong><span>{t('evaluation.replayed', 'rejoués')}</span></div>
                            {typeof score.golden_pass_rate === 'number' && (
                                <div className={score.golden_gate_passed ? '' : 'evp-bad'}><strong>{pct(score.golden_pass_rate)}</strong><span>{t('evaluation.golden', 'jeu de référence')} ({score.golden_cases})</span></div>
                            )}
                        </div>
                    )}
                    {evaluation.entries && evaluation.entries.length > 0 && (
                        <div className="evp-entries">
                            {evaluation.entries.map((e) => (
                                <details key={e.source_run_id} className={`evp-entry ${e.status}`}>
                                    <summary>
                                        <span className={`evp-pill ${e.status}`}>{t(`evaluation.entry.${e.status}`, e.status)}</span>
                                        {e.judge && typeof e.judge.same_decision === 'boolean' && (
                                            <span className={`evp-pill ${e.judge.same_decision ? 'ok' : 'diverged'}`}>{e.judge.same_decision ? t('evaluation.sameDecision', 'même décision') : t('evaluation.otherDecision', 'décision différente')}</span>
                                        )}
                                        <span className="evp-muted">{(e.replay_tools || []).map((x) => x.name).join(', ') || t('evaluation.noTools', 'aucun outil')}</span>
                                        {e.judge?.notes && <span className="evp-muted"> — {e.judge.notes}</span>}
                                    </summary>
                                    {e.reason && <div className="evp-muted">{e.reason}</div>}
                                    {e.error && <div className="evp-error">{e.error}</div>}
                                    {(e.source_text || e.replay_text) && (
                                        <div className="evp-compare">
                                            <div><h5>{t('evaluation.before', 'Réponse d’origine')}</h5><pre>{e.source_text}</pre></div>
                                            <div><h5>{t('evaluation.after', 'Réponse rejouée')}</h5><pre>{e.replay_text}</pre></div>
                                        </div>
                                    )}
                                </details>
                            ))}
                        </div>
                    )}
                    <div className="evp-actions">
                        {evaluation.status === 'done' && !evaluation.applied_at && (
                            <>
                                {goldenBlocked && <div className="evp-warning">{t('evaluation.goldenBlocked', 'Le jeu de référence de cet assistant échoue avec cette version : l’application est bloquée (vous pouvez forcer).')}</div>}
                                <button type="button" className="u-btn u-btn--primary" onClick={apply} disabled={busy}>
                                    <FaCheckCircle /> {goldenBlocked ? t('evaluation.applyForce', 'Appliquer malgré tout') : t('evaluation.apply', 'Appliquer cette version')}
                                </button>
                            </>
                        )}
                        {evaluation.applied_at && <span className="evp-applied">{t('evaluation.applied', 'Appliquée en version {{version}}', { version: evaluation.applied_version })}</span>}
                        {!pending && (
                            <button type="button" className="u-btn u-btn--secondary" onClick={() => setEvaluation(null)} disabled={busy}>
                                {t('evaluation.another', 'Autre proposition')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EvaluationPanel;
